import { getCurrencyFormatter, getNumberFormatter } from "./Intl";
import { getType, ObjectType } from "./Object";
import { isNullOrWhiteSpace } from "./String";

export function isNumber(value: unknown): value is number {
  return getType(value) === ObjectType.Number;
}

export function formatCurrency(locale: string, number: number, currency: string): string {
  if (number == null || number <= 0) {
    return null;
  }

  return getCurrencyFormatter(locale, currency).format(number);
}

export function formatNumber(locale: string, number: number, precision: number = undefined): string {
  if (number == null) {
    return null;
  }

  return getNumberFormatter(locale, true, precision).format(number);
}

export function getCurrencySymbol(locale: string, currency: string): string {
  if (isNullOrWhiteSpace(currency)) {
    console.error(`No currency provided.`);
    return `?`;
  }
  const currencyString = getCurrencyFormatter(locale, currency).format(1);

  if (isNullOrWhiteSpace(currencyString)) {
    console.error(`No currency conversion happened. Currency was: '${currency}'`);
    return `?`;
  }

  return currencyString.replaceAll(/[0-9.,\s]*/g, ``);
}

export enum CurrencyPosition {
  Front,
  Back
}

export function getCurrencyPosition(locale: string): CurrencyPosition {
  const parts = getCurrencyFormatter(locale, `EUR`).formatToParts(1);
  return parts[0].value === `€` ? CurrencyPosition.Front : CurrencyPosition.Back;
}

interface ISeparators {
  decimal: string;
  group: string;
}

function getSeparators(locale: string): ISeparators {
  const parts = getNumberFormatter(locale).formatToParts(1234.5);
  return {
    decimal: parts.find((part) => part.type === `decimal`).value,
    group: parts.find((part) => part.type === `group`).value
  };
}

function sanitizeNumberString(locale: string, value: string): string {
  const numerals = [...getNumberFormatter(locale, false).format(9876543210)].reverse();
  const numeralsRegEx = new RegExp(`[${numerals.join(``)}]`, `g`);
  const numeralsIndex = new Map(numerals.map((digit, index) => [digit, index]));
  const { decimal, group } = getSeparators(locale);
  return value
    ?.trim()
    ?.replaceAll(group, ``)
    ?.replace(decimal, `.`)
    ?.replace(numeralsRegEx, (d) => numeralsIndex.get(d).toString());
}

export function parseInteger(locale: string, value: string): number {
  if (isInvalidNumberString(locale, value)) {
    return NaN;
  }

  const sanitizedNumber = sanitizeNumberString(locale, value);

  if (isNullOrWhiteSpace(sanitizedNumber)) {
    return NaN;
  }

  return parseInt(sanitizedNumber, 10);
}

export function parseDecimal(locale: string, value: string): number {
  if (isInvalidNumberString(locale, value)) {
    return NaN;
  }

  const sanitizedNumber = sanitizeNumberString(locale, value);

  if (isNullOrWhiteSpace(sanitizedNumber)) {
    return NaN;
  }

  return parseFloat(sanitizedNumber);
}

export function isValidNumberString(locale: string, value: string): boolean {
  const { group, decimal } = getSeparators(locale);
  return new RegExp(`^[${group}${decimal}\\d]+$`).test(value);
}

export function isInvalidNumberString(locale: string, value: string): boolean {
  return !isValidNumberString(locale, value);
}

export function clampValue(value: number, bounds: { min: number; max: number }) {
  let output = value;

  const { max } = bounds;
  if (isNumber(max)) {
    output = Math.min(output, max);
  }

  const { min } = bounds;
  if (isNumber(min)) {
    output = Math.max(output, min);
  }

  return output;
}
